/* =====================================================================
   CosyNest — interactions & animations
   Ported from the static HTML build, adapted for Shopify:
   • add-to-cart posts to /cart/add.js (AJAX)
   • cart drawer refreshes via the section-rendering API (?section_id=cart-drawer)
   • qty / remove buttons post to /cart/change.js
   Robust: degrades gracefully if GSAP / Lenis fail to load.
   ===================================================================== */
(function () {
  "use strict";

  /* Signal <html> as JS-enabled ASAP. The [data-reveal] hide rule in
     cosynest.css is scoped to html.js, so if this script (or the CDN it uses)
     ever fails to load, content is never left invisible. */
  document.documentElement.classList.add("js");

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const clamp = (min, val, max) => Math.min(max, Math.max(min, val));
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const hasGSAP = typeof window.gsap !== "undefined";
  const hasST = hasGSAP && typeof window.ScrollTrigger !== "undefined";

  const onReady = (fn) =>
    document.readyState !== "loading" ? fn() : document.addEventListener("DOMContentLoaded", fn);

  onReady(init);

  function init() {
    // Isolate each setup so one throwing function can never stop the rest
    // (countdown, cart, reveals, etc. all keep working independently).
    const safe = (fn) => { try { fn(); } catch (e) { console.warn("[cosynest]", e); } };

    safe(setupAnnounce);
    safe(setupHeader);
    safe(setupDropdowns);
    safe(setupDrawers);
    safe(setupPredictiveSearch);
    safe(setupCarousel);
    safe(setupWishlist);
    safe(setupCart);       /* Shopify AJAX cart (add / change / refresh) */
    safe(setupQuickView);
    safe(setupNewsletter);
    safe(setupImageFallback);
    safe(setupCountdown);
    safe(setupFaq);
    safe(setupCollection);
    safe(setupMobileSearch);
    safe(setupFooterAccordion);
    safe(setupUspDots);

    /* Reveal + count-up ALWAYS run via IntersectionObserver, so page content
       is never left hidden — even if GSAP/ScrollTrigger or the CDN fails. */
    safe(fallbackReveal);
    safe(fallbackCounters);

    if (hasGSAP && hasST) {
      try {
        gsap.registerPlugin(ScrollTrigger);
        // setupSmoothScroll() intentionally NOT called: Lenis hijacks scrolling
        // and reliably breaks the Shopify editor iframe. Reveals use the
        // IntersectionObserver failsafe above instead.
        setupParallax();
        setupStackedCards();
      } catch (e) { console.warn("[cosynest] GSAP", e); }
    }
  }

  /* ---------- ANNOUNCEMENT BAR (copy code) ---------- */
  function setupAnnounce() {
    const btn = $(".code-btn[data-code]");
    if (!btn) return;

    const label = $(".code-btn-label", btn);
    btn.addEventListener("click", async () => {
      const code = btn.dataset.code || "";
      let copied = false;
      try { copied = await copyText(code); } catch (e) { /* fall through */ }
      if (!copied) copied = legacyCopy(code);
      if (!copied) { toast("Copy not supported — code: " + code); return; }
      toast("Code " + code + " copied to clipboard");
      if (label) {
        label.textContent = "Copied!";
        btn.classList.add("is-copied");
        setTimeout(() => { label.textContent = "Copy Code"; btn.classList.remove("is-copied"); }, 2000);
      }
    });

    async function copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      return false;
    }

    function legacyCopy(text) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try { ok = document.execCommand("copy"); } catch (e) { ok = false; }
      document.body.removeChild(ta);
      return ok;
    }
  }

  /* ---------- STICKY HEADER ---------- */
  function setupHeader() {
    const header = $("#header");
    const progress = $("#scrollProgress");
    if (!header) return;
    let last = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      header.classList.toggle("is-stuck", y > 8);
      // hide on scroll-down, reveal on scroll-up (only past the header)
      if (y > 260 && y > last + 4) header.classList.add("is-hidden");
      else if (y < last - 4 || y < 260) header.classList.remove("is-hidden");
      last = y;
      if (progress) {
        const h = document.documentElement.scrollHeight - window.innerHeight;
        progress.style.width = (h > 0 ? (y / h) * 100 : 0) + "%";
      }
      ticking = false;
    };
    window.addEventListener(
      "scroll",
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------- NAV DROPDOWNS ---------- */
  function setupDropdowns() {
    const items = $$(".nav__item.has-dropdown");
    const backdrop = $("#navBackdrop");
    if (!items.length) return;

    const sync = () => {
      if (backdrop) backdrop.classList.toggle("is-on", items.some((i) => i.classList.contains("is-open")));
    };
    const close = (item) => {
      item.classList.remove("is-open");
      const btn = item.querySelector(".nav__link");
      if (btn) btn.setAttribute("aria-expanded", "false");
      sync();
    };
    const open = (item) => {
      items.forEach((i) => {
        if (i === item) return;
        i.classList.remove("is-open");
        const b = i.querySelector(".nav__link");
        if (b) b.setAttribute("aria-expanded", "false");
      });
      item.classList.add("is-open");
      const btn = item.querySelector(".nav__link");
      if (btn) btn.setAttribute("aria-expanded", "true");
      sync();
    };

    items.forEach((item) => {
      item.addEventListener("mouseenter", () => open(item));
      item.addEventListener("mouseleave", () => close(item));
      const btn = item.querySelector(".nav__link");
      if (btn) {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          item.classList.contains("is-open") ? close(item) : open(item);
        });
      }
    });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") items.forEach(close); });
    if (backdrop) backdrop.addEventListener("click", () => items.forEach(close));
  }

  /* ---------- DRAWERS (mobile nav + cart open/close) ---------- */
  function setupDrawers() {
    const overlay = $("#overlay");
    const body = document.body;
    /* #cartDrawer is re-rendered by refreshCart() (replaceWith), so it must be
       looked up FRESH on every open/close. Holding the node from setup time
       closed a detached copy — the drawer then stayed stuck open. */
    const drawerEl = () => $("#drawer");
    const cartEl = () => $("#cartDrawer");

    const openDrawer = (target) => {
      if (!target) return;
      target.classList.add("is-open");
      target.setAttribute("aria-hidden", "false");
      overlay && overlay.classList.add("is-on");
      body.classList.add("no-scroll");
    };
    const closeDrawer = (target) => {
      if (!target) return;
      target.classList.remove("is-open");
      target.setAttribute("aria-hidden", "true");
      /* only drop the overlay + scroll lock once nothing needs them */
      const anyOpen = [drawerEl(), cartEl()].some((d) => d && d.classList.contains("is-open"));
      if (!anyOpen) {
        overlay && overlay.classList.remove("is-on");
        body.classList.remove("no-scroll");
      }
    };

    window.__closeCart = () => closeDrawer(cartEl());
    window.__closeDrawer = () => closeDrawer(drawerEl());

    const burger = $("#burger");
    if (burger) burger.addEventListener("click", (e) => { e.preventDefault(); openDrawer(drawerEl()); });
    const cartBtn = $("#cartBtn");
    if (cartBtn) cartBtn.addEventListener("click", (e) => { e.preventDefault(); openCart(); });

    /* Close clicks are DELEGATED and the target drawer is resolved from the
       CLICKED BUTTON's own tree (falling back to a fresh lookup): delegation
       alone wasn't enough, because refreshCart() replaces the whole #cartDrawer
       node and any captured reference goes stale with it. */
    document.addEventListener("click", (e) => {
      const closeBtn = e.target.closest("[data-cart-close], #cartClose");
      if (closeBtn) { e.preventDefault(); closeDrawer(closeBtn.closest("#cartDrawer") || cartEl()); return; }
      const drawerClose = e.target.closest("#drawerClose");
      if (drawerClose) { e.preventDefault(); closeDrawer(drawerClose.closest("#drawer") || drawerEl()); }
    });
    if (overlay) overlay.addEventListener("click", () => { closeDrawer(drawerEl()); closeDrawer(cartEl()); });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { closeDrawer(drawerEl()); closeDrawer(cartEl()); closeQV(); }
    });
  }

  /* ---------- MOBILE SEARCH TOGGLE ---------- */
  function setupMobileSearch() {
    const header = $("#header");
    const toggle = $("#searchToggle");
    if (!header || !toggle) return;
    const mq = window.matchMedia("(max-width:860px)");
    const close = () => {
      header.classList.remove("is-search-open");
      toggle.setAttribute("aria-expanded", "false");
    };
    toggle.addEventListener("click", () => {
      if (!mq.matches) return;                 // desktop keeps the inline search
      const open = header.classList.toggle("is-search-open");
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) {
        const input = header.querySelector("#search input");
        if (input) window.setTimeout(() => input.focus(), 60);
      }
    });
    mq.addEventListener("change", close);
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });
  }

  /* ---------- PREDICTIVE SEARCH (header suggestions) ----------
     Queries the storefront predictive-search API as the customer types and
     renders product suggestions inside the dropdown. Falls back silently —
     the form still submits to /search normally. */
  function setupPredictiveSearch() {
    const form = $("form[data-predictive]");
    if (!form) return;
    const input = form.querySelector('input[name="q"]');
    const panel = form.querySelector(".search__predictive");
    if (!input || !panel) return;

    let controller = null;      // aborts the in-flight request on new keystrokes
    let debounce = null;
    let activeIndex = -1;       // keyboard navigation (↑/↓/Enter)

    const moneyValue = (value) => {
      if (value == null || value === "") return null;
      const raw = String(value);
      const numeric = Number(raw.replace(/[^0-9.-]/g, ""));
      if (!Number.isFinite(numeric)) return null;

      /* Predictive search returns decimal prices, while cart/product JSON can
         return integer cents. Support both so suggestions show real prices. */
      return typeof value === "number" && Number.isInteger(numeric) && numeric >= 1000 ? numeric / 100 : numeric;
    };

    const fmt = (value) => {
      const amount = moneyValue(value);
      if (amount == null) return "";
      return "£" + amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const closePanel = () => {
      panel.hidden = true;
      input.setAttribute("aria-expanded", "false");
      activeIndex = -1;
    };

    const render = (products, terms) => {
      if (!products.length) {
        panel.innerHTML =
          '<p class="search__predictive-empty">No matches — press Enter to search everything</p>';
        return;
      }
      const items = products.map((p, i) => {
        const img = p.featured_image && p.featured_image.url
          ? p.featured_image.url.replace(/&width=\d+|width=\d+/, "") + "&width=120"
          : "";
        const minPrice = moneyValue(p.price_min != null ? p.price_min : p.price);
        const maxPrice = moneyValue(p.price_max);
        const compareAmount = moneyValue(p.compare_at_price_min || p.compare_at_price);
        const price = minPrice != null ? (maxPrice && maxPrice > minPrice ? "From " : "") + fmt(minPrice) : "";
        const compare = compareAmount && minPrice && compareAmount > minPrice ? fmt(compareAmount) : "";
        return (
          '<a class="search__predictive-item" role="option" href="' + p.url + '" id="ps-opt-' + i + '">' +
            (img ? '<span class="search__predictive-img"><img src="' + img + '" alt="" loading="lazy" width="60" height="60"></span>' : "") +
            '<span class="search__predictive-info">' +
              '<strong>' + p.title + "</strong>" +
              (p.vendor ? '<small>' + p.vendor + "</small>" : "") +
            "</span>" +
            '<span class="search__predictive-price">' + price +
              (compare ? "<s>" + compare + "</s>" : "") +
            "</span>" +
          "</a>"
        );
      }).join("");
      panel.innerHTML =
        '<div class="search__predictive-head">Suggestions</div>' +
        items +
        '<a class="search__predictive-all" role="option" href="' + (window.routes && window.routes.search_url || "/search") + "?q=" + encodeURIComponent(terms) + '">See all results for "' + terms + '"</a>';
    };

    const query = async (terms) => {
      if (controller) controller.abort();
      controller = new AbortController();
      const url =
        "/search/suggest.json?q=" + encodeURIComponent(terms) +
        "&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,tag";
      try {
        const res = await fetch(url, { credentials: "same-origin", signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        render(products, terms);
        panel.hidden = false;
        input.setAttribute("aria-expanded", "true");
      } catch (err) {
        if (err.name !== "AbortError") closePanel();
      }
    };

    input.addEventListener("input", () => {
      const terms = input.value.trim();
      clearTimeout(debounce);
      if (terms.length < 2) { closePanel(); return; }
      debounce = setTimeout(() => query(terms), 220);
    });

    input.addEventListener("focus", () => {
      const terms = input.value.trim();
      if (terms.length >= 2 && !panel.hidden) return;
      if (terms.length >= 2) query(terms);
    });

    /* keyboard nav: ↑/↓ move, Enter opens, Escape closes */
    input.addEventListener("keydown", (e) => {
      if (panel.hidden) return;
      const options = Array.from(panel.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        activeIndex = e.key === "ArrowDown"
          ? (activeIndex + 1) % options.length
          : (activeIndex - 1 + options.length) % options.length;
        options.forEach((o, i) => {
          o.classList.toggle("is-active", i === activeIndex);
          if (i === activeIndex) {
            o.setAttribute("aria-selected", "true");
            input.setAttribute("aria-activedescendant", o.id);
          } else {
            o.removeAttribute("aria-selected");
          }
        });
      } else if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        options[activeIndex].click();
      } else if (e.key === "Escape") {
        closePanel();
      }
    });

    /* click outside closes */
    document.addEventListener("click", (e) => {
      if (!form.contains(e.target)) closePanel();
    });
  }

  /* ---------- FOOTER ACCORDION (mobile only) ---------- */
  function setupFooterAccordion() {
    const cols = $$(".footer__col");
    if (!cols.length) return;
    const mq = window.matchMedia("(max-width:620px)");
    const sync = () => {
      cols.forEach((col) => {
        const btn = col.querySelector(".footer__title");
        if (!btn) return;
        const mobile = mq.matches;
        col.classList.toggle("is-open", !mobile);   // open on desktop, closed on mobile
        btn.setAttribute("aria-expanded", mobile ? "false" : "true");
      });
    };
    cols.forEach((col) => {
      const btn = col.querySelector(".footer__title");
      if (!btn) return;
      btn.addEventListener("click", () => {
        if (!mq.matches) return;                    // only interactive on mobile
        const open = col.classList.toggle("is-open");
        btn.setAttribute("aria-expanded", open ? "true" : "false");
      });
    });
    sync();
    mq.addEventListener("change", sync);
  }

  /* ---------- USP CAROUSEL DOTS (mobile) ---------- */
  function setupUspDots() {
    const track = $(".usp__grid");
    const dotsWrap = $(".usp__dots");
    if (!track || !dotsWrap) return;
    const items = Array.from(track.querySelectorAll(".usp__item"));
    if (items.length < 2) return;
    items.forEach((_, i) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "usp__dot";
      b.setAttribute("aria-label", "Go to promise " + (i + 1));
      b.addEventListener("click", () => {
        track.scrollTo({ left: items[i].offsetLeft, behavior: prefersReduced ? "auto" : "smooth" });
      });
      dotsWrap.appendChild(b);
    });
    const dots = Array.from(dotsWrap.children);
    let ticking = false;
    const sync = () => {
      const i = Math.round(track.scrollLeft / (track.clientWidth || 1));
      dots.forEach((d, idx) => d.classList.toggle("is-active", idx === i));
      ticking = false;
    };
    track.addEventListener("scroll", () => {
      if (!ticking) { window.requestAnimationFrame(sync); ticking = true; }
    }, { passive: true });
    sync();
  }

  /* ---------- CAROUSEL ---------- */
  function setupCarousel() {
    $$("[data-dir]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const track = document.getElementById(btn.dataset.target);
        if (!track) return;
        const dir = parseInt(btn.dataset.dir, 10);
        const card = track.querySelector(".product, .review, article, .insta__cell");
        const step = card ? card.offsetWidth + 24 : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * step, behavior: "smooth" });
      });
    });

    // drag-to-scroll on carousels (pointer)
    $$("[data-carousel]").forEach((wrap) => {
      const track = wrap.querySelector(".carousel__track");
      if (!track) return;
      let down = false, startX = 0, startScroll = 0;
      track.addEventListener("pointerdown", (e) => {
        if (e.pointerType === "mouse" && e.target.closest("button, a")) return;
        down = true; track.setPointerCapture(e.pointerId);
        startX = e.clientX; startScroll = track.scrollLeft;
        track.style.cursor = "grabbing";
      });
      track.addEventListener("pointermove", (e) => {
        if (!down) return;
        track.scrollLeft = startScroll - (e.clientX - startX);
      });
      const end = () => { down = false; track.style.cursor = ""; };
      track.addEventListener("pointerup", end);
      track.addEventListener("pointercancel", end);
    });
  }

  /* ---------- WISHLIST ---------- */
  function setupWishlist() {
    const countEl = $("#wishCount");
    let count = 0;
    $$("[data-wish]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const active = btn.classList.toggle("is-active");
        count += active ? 1 : -1;
        if (countEl) {
          countEl.textContent = count;
          countEl.style.transform = "scale(1.4)";
          setTimeout(() => (countEl.style.transform = ""), 180);
        }
        toast(active ? "Saved to your wishlist" : "Removed from wishlist");
      });
    });
  }

  /* =================================================================
     SHOPIFY AJAX CART
     ================================================================= */
  function setCounts(n) {
    ["#cartCount", "#cartCount2"].forEach((sel) => {
      const el = $(sel);
      if (!el) return;
      el.textContent = n;
      el.style.transform = "scale(1.4)";
      setTimeout(() => (el.style.transform = ""), 180);
    });
  }

  async function refreshCart(open) {
    try {
      const [cartRes, sectionRes] = await Promise.all([
        fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json()),
        fetch("/cart?section_id=cart-drawer", { credentials: "same-origin" }).then((r) => r.text()),
      ]);
      setCounts(cartRes.item_count || 0);
      const doc = new DOMParser().parseFromString(sectionRes, "text/html");
      const fresh = doc.querySelector("#cartDrawer");
      const current = document.getElementById("cartDrawer");
      /* The re-rendered section arrives closed. If the drawer is already open
         (qty change, remove…), it must STAY open — only swap the inner body. */
      if (fresh && current) {
        const wasOpen = current.classList.contains("is-open");
        if (wasOpen) {
          fresh.classList.add("is-open");
          fresh.setAttribute("aria-hidden", "false");
        }
        current.replaceWith(fresh);
      }
      if (open) openCart();
    } catch (err) {
      // network/section error — fall back to a full cart redirect on next action
      console.warn("Cart refresh failed", err);
    }
  }

  function openCart() {
    const cart = $("#cartDrawer");
    const overlay = $("#overlay");
    if (!cart) return;
    cart.classList.add("is-open");
    cart.setAttribute("aria-hidden", "false");
    overlay && overlay.classList.add("is-on");
    document.body.classList.add("no-scroll");
  }

  function setupCart() {
    /* Add to basket — works for product cards and the quick-view modal.
       Delegated on document so dynamically injected forms are covered. */
    document.addEventListener("submit", async (e) => {
      const form = e.target.closest("[data-product-form]");
      if (!form) return;
      e.preventDefault();
      const card = form.closest(".product");
      const name = (card && card.querySelector(".product__name") && card.querySelector(".product__name").textContent.trim()) || "Item";
      const btn = form.querySelector("[data-add]");
      if (btn) { btn.disabled = true; btn.dataset.label = btn.textContent; btn.textContent = "Adding…"; }
      try {
        await fetch("/cart/add.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });
        toast(name + " added to basket");
        await refreshCart(true);
        if (document.querySelector(".cart-page__items")) await refreshCartPage();
        /* lets product-services-addons.js re-link / clean service & add-on lines */
        document.dispatchEvent(new CustomEvent("cosynest:cart-changed"));
      } catch (err) {
        toast("Sorry, that didn't work — try again");
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || "Add to basket"; }
      }
    });

    /* Qty steppers + remove. Delegated on document so they keep working on the
       /cart page AND inside the drawer after refreshCart() replaces its HTML.
       Uses item keys (not line indexes) so re-renders can't shift the target. */
    document.addEventListener("click", async (e) => {
      const qtyBtn = e.target.closest(".cart__qty button");
      if (qtyBtn) {
        e.preventDefault();
        const key = qtyBtn.dataset.key;
        const dir = parseInt(qtyBtn.dataset.step, 10);
        const span = qtyBtn.parentElement.querySelector("span");
        let q = parseInt(span ? span.textContent : "1", 10) || 1;
        q = clamp(1, q + dir, 99);
        await changeLine(key, q);
        return;
      }

      const rm = e.target.closest(".cart__remove[data-key]");
      if (rm) {
        e.preventDefault();
        await changeLine(rm.dataset.key, 0);
      }
    });
  }

  async function changeLine(key, quantity) {
    try {
      await fetch("/cart/change.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });
      await refreshCart(false);
      /* /cart page: re-render its section too so totals + grouping stay live */
      const pageItems = document.querySelector(".cart-page__items");
      if (pageItems) await refreshCartPage();
      /* removing a main product can orphan its service / add-on lines */
      document.dispatchEvent(new CustomEvent("cosynest:cart-changed"));
    } catch (err) {
      console.warn("Cart change failed", err);
    }
  }

  /* Re-renders the /cart page in place via the section-rendering API.
     NOTE: the section id is the section FILENAME ("cart"), not the JSON
     template key ("main") — ?section_id=main 404s on JSON templates. */
  async function refreshCartPage() {
    try {
      const res = await fetch("/cart?section_id=cart", { credentials: "same-origin" });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      const fresh = doc.querySelector(".cart-page");
      const current = document.querySelector(".cart-page");
      if (fresh && current) current.replaceWith(fresh);
    } catch (err) {
      /* section swap failed — the next full page load still shows truth */
      console.warn("Cart page refresh failed", err);
    }
  }

  /* ---------- QUICK VIEW ---------- */
  let closeQV = () => {};
  function setupQuickView() {
    const modal = $("#qvModal");
    if (!modal) return;

    const fill = (card) => {
      if (!card) return;
      const img = card.querySelector(".product__img");
      const name = (card.querySelector(".product__name") && card.querySelector(".product__name").textContent.trim()) || "Product";
      const qvImg = $("#qvImg");
      if (qvImg && img) { qvImg.src = img.src; qvImg.alt = name; }
      const kicker = $("#qvKicker");
      if (kicker) kicker.textContent = (card.querySelector(".product__meta") && card.querySelector(".product__meta").textContent.trim()) || "";
      const qvName = $("#qvName");
      if (qvName) qvName.textContent = name;
      const rating = card.querySelector(".product__rating span");
      const starSpan = $("#qvStars span");
      if (rating && starSpan) starSpan.textContent = rating.textContent.trim();
      const now = card.querySelector(".price__now");
      const wasEl = card.querySelector(".price__was");
      const save = card.querySelector(".price__save");
      const rrpLine = card.querySelector(".price__rrp-line");
      const rrpMatch = rrpLine && rrpLine.textContent ? rrpLine.textContent.match(/RRP\s*([£$€][\d,]+)/i) : null;
      $("#qvPrice").innerHTML =
        (now ? `<span class="price__now">${now.textContent.trim()}</span>` : "") +
        (wasEl ? `<span class="price__was">${wasEl.textContent.trim()}</span>` : "") +
        (rrpMatch ? `<span class="price__rrp">RRP ${rrpMatch[1]}</span>` : "") +
        (save ? `<span class="price__save">${save.textContent.trim()}</span>` : "");
      /* wire the QV add-to-cart form to this product's variant */
      const idInput = $("#qvVariantId");
      if (idInput) idInput.value = card.dataset.variantId || "";
    };

    const open = (card) => {
      fill(card || $(".product"));
      modal.classList.add("is-open");
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("no-scroll");
    };
    closeQV = () => {
      modal.classList.remove("is-open");
      modal.setAttribute("aria-hidden", "true");
      document.body.classList.remove("no-scroll");
    };

    $$("[data-quickview]").forEach((btn) =>
      btn.addEventListener("click", (e) => { e.preventDefault(); open(btn.closest(".product")); })
    );
    const qvClose = $("#qvClose");
    if (qvClose) qvClose.addEventListener("click", closeQV);
    modal.addEventListener("click", (e) => { if (e.target === modal || e.target.classList.contains("qv__media") || e.target.classList.contains("qv__panel")) closeQV(); });

    // size buttons (visual toggle only)
    $$(".qv__sizes button").forEach((b) =>
      b.addEventListener("click", () => {
        $$(".qv__sizes button").forEach((x) => x.classList.remove("is-active"));
        b.classList.add("is-active");
      })
    );
  }

  /* ---------- NEWSLETTER ---------- */
  function setupNewsletter() {
    const form = $("#newsForm");
    if (!form) return;
    /* If this is a real Shopify customer form, let it submit natively and rely
       on the success message rendered by Liquid. Otherwise validate client-side. */
    if (form.dataset.shopify === "true") return;
    const note = $("#newsNote");
    form.addEventListener("submit", (e) => {
      const email = $("#newsEmail");
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim());
      if (!ok) {
        e.preventDefault();
        note.textContent = "Please enter a valid email address.";
        note.classList.remove("is-ok");
        email.focus();
        return;
      }
      note.textContent = "You're in. Check your inbox for your welcome code.";
      note.classList.add("is-ok");
    });
  }

  /* ---------- IMAGE FALLBACK ---------- */
  function setupImageFallback() {
    $$("img").forEach((img) => {
      if (img.complete) return;
      img.addEventListener("error", () => img.classList.add("img-fail"), { once: true });
    });
  }

  /* ---------- SALE COUNTDOWN (evergreen + auto-reset) ---------- */
  function setupCountdown() {
    try {
      const root = $("#countdown");
      if (!root) return;
      const cells = {
        days: root.querySelector('[data-cd="days"]'),
        hours: root.querySelector('[data-cd="hours"]'),
        mins: root.querySelector('[data-cd="mins"]'),
        secs: root.querySelector('[data-cd="secs"]'),
      };
      if (!cells.secs) return;
      const pad = (n) => String(n).padStart(2, "0");

      // Evergreen rolling countdown (~2d 8h 23m 11s). When it hits zero it
      // auto-resets forward, so the timer NEVER sits at 00:00:00:00. Persisted
      // in localStorage so it stays consistent across reloads and tabs. A fixed
      // merchant end-date (data-end on #countdown) takes precedence and, once
      // reached, holds at zero.
      const DURATION = (2 * 86400 + 8 * 3600 + 23 * 60 + 11) * 1000;
      const KEY = "topbeds_sale_end";
      const fixedEnd = root.dataset.end ? Date.parse(root.dataset.end) : NaN;
      const hasFixed = isFinite(fixedEnd);
      let end;
      if (hasFixed) {
        end = fixedEnd;
      } else {
        end = parseInt(localStorage.getItem(KEY), 10);
        if (!isFinite(end) || end < Date.now()) end = Date.now() + DURATION;
        try { localStorage.setItem(KEY, String(end)); } catch (e) {}
      }

      const tick = () => {
        let diff = end - Date.now();
        if (diff <= 0) {
          if (hasFixed) {
            diff = 0; // fixed end reached — hold at zero
          } else {
            end = Date.now() + DURATION; // auto-reset, keep counting forever
            try { localStorage.setItem(KEY, String(end)); } catch (e) {}
            diff = end - Date.now();
          }
        }
        let d = Math.floor(diff / 86400000); diff -= d * 86400000;
        let h = Math.floor(diff / 3600000); diff -= h * 3600000;
        let m = Math.floor(diff / 60000); diff -= m * 60000;
        let s = Math.floor(diff / 1000);
        cells.days.textContent = pad(d);
        cells.hours.textContent = pad(h);
        cells.mins.textContent = pad(m);
        cells.secs.textContent = pad(s);
      };
      tick();
      setInterval(tick, 1000);
    } catch (e) {
      console.warn("[cosynest] countdown failed", e);
    }
  }

  /* ---------- TOAST ---------- */
  let toastTimer;
  function toast(msg) {
    const el = $("#toast");
    if (!el) return;
    el.textContent = msg;
    el.classList.add("is-show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove("is-show"), 2600);
  }

  /* =================================================================
     GSAP-POWERED ANIMATIONS
     ================================================================= */
  function setupSmoothScroll() {
    if (prefersReduced) return;
    const Lenis = window.Lenis;
    if (!Lenis) return;
    const lenis = new Lenis({ duration: 1.05, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true });
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // anchor links via Lenis
    $$('a[href^="#"]').forEach((a) => {
      a.addEventListener("click", (e) => {
        const id = a.getAttribute("href");
        if (id.length > 1) {
          const t = document.querySelector(id);
          if (t) { e.preventDefault(); lenis.scrollTo(t, { offset: -120 }); }
        }
      });
    });
  }

  function setupReveals() {
    const els = $$("[data-reveal]");
    if (prefersReduced) { gsap.set(els, { opacity: 1, y: 0 }); return; }
    gsap.utils.toArray(els).forEach((el) => {
      const delay = parseFloat(el.dataset.delay || "0");
      gsap.fromTo(
        el,
        { y: 30, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.85, delay, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 88%", once: true },
        }
      );
    });
  }

  function setupParallax() {
    if (prefersReduced) return;
    $$("[data-parallax]").forEach((el) => {
      const amt = parseFloat(el.dataset.parallax) * 100;
      gsap.to(el, {
        yPercent: amt, ease: "none",
        scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: true },
      });
    });
  }

  function setupCounters() {
    $$("[data-count]").forEach((el) => {
      const target = parseFloat(el.dataset.count);
      const obj = { v: 0 };
      ScrollTrigger.create({
        trigger: el, start: "top 92%", once: true,
        onEnter: () => gsap.to(obj, {
          v: target, duration: 1.8, ease: "power2.out",
          onUpdate: () => { el.textContent = formatNum(obj.v, target); },
        }),
      });
    });
  }
  function formatNum(v, target) {
    if (target >= 1000) return Math.round(v).toLocaleString("en-GB");
    return Math.round(v).toString();
  }

  function setupStackedCards() {
    const stack = $("#collStack");
    if (!stack) return;
    const mm = gsap.matchMedia();

    mm.add("(min-width: 901px) and (prefers-reduced-motion: no-preference)", () => {
      const cards = gsap.utils.toArray(".stack__card", stack);
      if (cards.length < 2) return;
      const N = cards.length;
      const seg = 1 / (N - 1);
      stack.classList.add("is-pinned");

      cards.forEach((card, i) => {
        const rot = parseFloat(card.dataset.rotation || "0");
        gsap.set(card, {
          y: i === 0 ? 0 : window.innerHeight * 0.6,
          opacity: i === 0 ? 1 : 0,
          scale: 1, rotation: rot, zIndex: i,
        });
      });

      const place = (progress) => {
        cards.forEach((card, i) => {
          const rot = parseFloat(card.dataset.rotation || "0");
          let y = 0, opacity = 1, scale = 1;
          if (i > 0) {
            const ep = clamp(0, (progress - (i - 1) * seg) / seg, 1);
            y = (1 - ep) * window.innerHeight * 0.6;
            opacity = ep;
          }
          if (i < N - 1) {
            const cp = clamp(0, (progress - i * seg) / seg, 1);
            scale = 1 - cp * 0.05;
            opacity = Math.min(opacity, 1 - cp * 0.45);
          }
          gsap.set(card, { y, opacity, scale, rotation: rot * (i === 0 ? 1 - 0 : 1) });
        });
      };

      const headerH = ($("#header")?.offsetHeight || 160);
      const st = ScrollTrigger.create({
        trigger: stack, start: "top " + headerH,
        end: () => "+=" + (N - 1) * window.innerHeight * 0.9,
        pin: ".stack__pin", pinSpacing: true, scrub: 1, anticipatePin: 1,
        onUpdate: (self) => place(self.progress),
        onLeaveBack: () => place(0),
      });

      place(0);
      return () => { stack.classList.remove("is-pinned"); st && st.kill(); cards.forEach((c) => gsap.set(c, { clearProps: "all" })); };
    });
  }

  /* ---------- FALLBACKS (no GSAP) ---------- */
  function fallbackReveal() {
    const els = $$("[data-reveal]");
    if (!els.length) return;
    const reveal = (el) => el.classList.remove("is-hidden");
    const revealAll = () => els.forEach(reveal);

    if (prefersReduced || !("IntersectionObserver" in window)) { revealAll(); return; }

    /* Hide now (no transition = instant), then fade in on scroll into view. */
    els.forEach((e) => e.classList.add("is-hidden"));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { reveal(en.target); io.unobserve(en.target); } });
    }, { threshold: 0, rootMargin: "0px 0px -8% 0px" });
    els.forEach((e) => io.observe(e));

    /* HARD FAILSAFE: if the observer never fires (editor iframe, etc.), reveal
       anything still hidden after ~1.5s, on load, and on resize. */
    const failsafe = () => { els.forEach((e) => { if (e.classList.contains("is-hidden")) reveal(e); }); };
    setTimeout(failsafe, 1500);
    window.addEventListener("load", () => setTimeout(failsafe, 300));
    window.addEventListener("resize", failsafe, { passive: true });
  }

  function setupFaq() {
    // Accordion: one open at a time, with a smooth height animation.
    $$(".faq__list").forEach((list) => {
      const items = $$("details.faq__item", list);
      const panel = (item) => item.querySelector(".faq__a");

      items.forEach((item) => {
        const p = panel(item);
        if (!p) return;
        p.style.transition = "height .32s var(--ease), opacity .32s var(--ease)";
        p.style.overflow = "hidden";
      });

      const openItem = (item) => {
        const p = panel(item);
        item.open = true;
        p.style.height = "0px";
        p.style.opacity = "0";
        requestAnimationFrame(() => {
          p.style.height = p.scrollHeight + "px";
          p.style.opacity = "1";
        });
        const done = (e) => {
          if (e.propertyName !== "height") return;
          p.style.height = "auto";
          p.removeEventListener("transitionend", done);
        };
        p.addEventListener("transitionend", done);
      };

      const closeItem = (item) => {
        const p = panel(item);
        p.style.height = p.scrollHeight + "px";
        requestAnimationFrame(() => {
          p.style.height = "0px";
          p.style.opacity = "0";
        });
        const done = (e) => {
          if (e.propertyName !== "height") return;
          item.open = false;
          p.removeEventListener("transitionend", done);
        };
        p.addEventListener("transitionend", done);
      };

      items.forEach((item) => {
        const summary = item.querySelector("summary");
        if (!summary) return;
        summary.addEventListener("click", (e) => {
          e.preventDefault();
          if (!item.open) {
            items.forEach((o) => { if (o !== item && o.open) closeItem(o); });
            openItem(item);
          } else {
            closeItem(item);
          }
        });
      });
    });
  }

  function setupCollection() {
    const grid = $(".collection__grid");
    const toggle = $(".view-toggle");
    if (!grid || !toggle || toggle.dataset.viewToggleReady === "1") return;
    toggle.dataset.viewToggleReady = "1";

    const buttons = $$("[data-view]", toggle);
    const applyView = (view) => {
      const isList = view === "list";
      grid.classList.toggle("collection__grid--list", isList);
      buttons.forEach((btn) => {
        const active = btn.dataset.view === view;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
      });
      try { localStorage.setItem("topbeds_collection_view", view); } catch (e) {}
    };

    buttons.forEach((btn) => {
      btn.type = "button";
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        applyView(btn.dataset.view === "list" ? "list" : "grid");
      });
    });

    let saved = "";
    try { saved = localStorage.getItem("topbeds_collection_view") || ""; } catch (e) {}
    applyView(saved === "list" ? "list" : "grid");
  }

  function fallbackCounters() {
    $$("[data-count]").forEach((el) => {
      const target = parseFloat(el.dataset.count);
      const run = () => {
        const start = performance.now();
        const step = (now) => {
          const p = clamp(0, (now - start) / 1600, 1);
          el.textContent = formatNum(target * easeOut(p), target);
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };
      if (!("IntersectionObserver" in window)) return run();
      const io = new IntersectionObserver((en) => { if (en[0].isIntersecting) { run(); io.disconnect(); } }, { threshold: 0.4 });
      io.observe(el);
    });
  }
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
})();
